import React, { useEffect, useMemo, useState } from "react";
import { Form, InputGroup, Spinner } from "react-bootstrap";
import Select, { ContainerProps, ControlProps, CSSObjectWithLabel, GroupBase } from "react-select";
import { useCampaign } from "../../contexts/useCampaign";
import { Campaign } from "../../types";

interface CampaignListProps {
  onCampaignSelect: (campaign: Campaign | undefined) => void;
}

const CampaignList: React.FC<CampaignListProps> = ({ onCampaignSelect }) => {
  const [selectedOption, setSelectedOption] = useState<{ value: number; label: string } | null>(null);
  const { loadingCampaigns, loadCampaigns, campaigns } = useCampaign();

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Memoize options to prevent unnecessary re-renders of the Select component
  const options = useMemo(() => campaigns.map(campaign => ({
    value: campaign.id,
    label: `${campaign.name} (${campaign.status})`
  })), [campaigns]);

  // Handle selection changes
  useEffect(() => {
    const campaign = selectedOption
      ? campaigns.find(c => c.id === selectedOption.value)
      : undefined;

    onCampaignSelect(campaign);
  }, [selectedOption, campaigns]); // Removed onCampaignSelect from deps to avoid loops if not memoized by parent

  const customStyles = {
    control: (provided: CSSObjectWithLabel, state: ControlProps<{ value: number; label: string }, false, GroupBase<{ value: number; label: string }>>) => ({
      ...provided,
      minHeight: '38px',
      borderRadius: '0 4px 4px 0',
      border: '1px solid #dee2e6',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#dee2e6',
      }
    }),
    container: (provided: CSSObjectWithLabel, state: ContainerProps<{ value: number; label: string }, false, GroupBase<{ value: number; label: string }>>) => ({
      ...provided,
      flex: 1,
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 9999 })
  };

  return (
    <div>
      <Form.Group className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <span className="text-success">📋</span>
          </InputGroup.Text>
          <Select
            id="campaign-select"
            instanceId="campaign-select" // Important for SSR and consistent IDs
            value={selectedOption}
            onChange={(option) => setSelectedOption(option as { value: number; label: string } | null)}
            options={options}
            placeholder={loadingCampaigns ? "Loading campaigns..." : "Search or choose a campaign..."}
            isSearchable
            isLoading={loadingCampaigns}
            isDisabled={loadingCampaigns || campaigns.length === 0}
            styles={customStyles}
            menuPortalTarget={document.body} // Prevents clipping and potential "stuck" UI issues
            noOptionsMessage={() => campaigns.length === 0 ? "No campaigns available" : "No matches found"}
          />
          {loadingCampaigns && (
            <InputGroup.Text>
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
        </InputGroup>
      </Form.Group>
    </div>
  );
};

export default CampaignList;

